import './styles.scoped.css'
import {useState, useEffect} from 'react'
import {Link, useLocation} from 'react-router-dom'
import {Typography, Modal, Select, MenuItem} from '@mui/material'
import {
    Dashboard,
    People,
    SelfImprovement,
    HealthAndSafety,
    CalendarToday,
    AssignmentTurnedIn,
    ReceiptLong,
    AutoGraph,
    Settings,
    SupportAgent,
    AdminPanelSettings,
    Business,
    GroupAdd,
    Loop,
    ArrowBack,
    Logout,
    ForkRight,
} from '@mui/icons-material'
import {useAuth} from '../../contexts/AuthContext'
import {careviahLogo} from '../../assets'
import {useMobile} from '../../hooks'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'

export default () => {
    const {permissions, branches} = useAuth()
    const location = useLocation()
    const [expandedMenus, setExpandedMenus] = useState([])
    const [isShowMainMenu, setIsShowMainMenu] = useState(true)
    const [bottomMenuItems, setBottomMenuItems] = useState([])
    const [filteredSettingMenuItems, setFilteredSettingMenuItems] = useState([])
    const [filteredMenuItems, setFilteredMenuItems] = useState([])
    const {mobileSidebar, setMobileSidebar, setLogout, branchId, setBranchId} = useGlobalStates()
    const {mobile} = useMobile()

    useEffect(() => {
        if (branches?.length > 0) {
            setBranchId(branches?.find(a => a?.is_primary)?.branch_id)
        }
    }, [branches])

    useEffect(() => {
        if (permissions) {
            const menuItems = [
                {label: 'Dashboard', icon: <Dashboard />, path: '/', permissions},
                {
                    label: 'Clients Management',
                    icon: <People />,
                    path: '/clients-management',
                    permissions: ['clients.read'],
                },
                {
                    label: 'Caregivers Management',
                    icon: <SelfImprovement />,
                    path: '/caregivers-management',
                    permissions: ['employees.read'],
                },
                {
                    label: 'Service Management',
                    icon: <HealthAndSafety />,
                    path: '/service-management',
                    permissions: ['services.read'],
                },
                {label: 'Scheduling', icon: <CalendarToday />, path: '/scheduling', permissions: ['schedules.read']},
                {label: 'EVV', icon: <AssignmentTurnedIn />, path: '/evv', permissions: ['evv.read']},
                {label: 'Billing', icon: <ReceiptLong />, path: '/billing', permissions: ['billing.read']},
                {
                    label: 'Analytics',
                    icon: <AutoGraph />,
                    path: '/analytics',
                    permissions: ['reports.financial', 'reports.hr', 'reports.clinical', 'reports.compliance'],
                },
            ]

            setBottomMenuItems([
                {label: 'Settings', icon: <Settings />, path: '/account-settings', permissions},
                {label: 'Help & Support', icon: <SupportAgent />, path: '/help-and-support', permissions},
            ])

            const settingsMenuItems = [
                {
                    label: 'Account Settings',
                    icon: <AdminPanelSettings />,
                    path: '/account-settings',
                    permissions,
                },
                {
                    label: 'Organization Settings',
                    icon: <Business />,
                    path: '/organization-settings',
                    permissions: ['admin.settings.read'],
                },
                {
                    label: 'Branch Management',
                    icon: <ForkRight />,
                    path: '/branch-management',
                    permissions: ['admin.settings.read'],
                },
                {label: 'Team Management', icon: <GroupAdd />, path: '/team-management', permissions: ['admin.users.read', 'admin.roles.read']},
                {label: 'Integration Settings', icon: <Loop />, path: '/integration-settings', permissions: ['admin.integrations']},
            ]

            const filteredMenuItemsTemp = menuItems.filter(item => item.permissions?.some(role => permissions?.includes(role)))

            setFilteredMenuItems(filteredMenuItemsTemp)

            setFilteredSettingMenuItems(settingsMenuItems.filter(item => item.permissions?.some(role => permissions?.includes(role))))

            const matchedParents = filteredMenuItemsTemp
                .filter(item => item.hasSubmenu)
                .filter(item => filteredSubmenu(item.submenu).some(sub => location.pathname.startsWith(sub.path)))
                .map(item => item.label)

            setExpandedMenus(matchedParents)

            setIsShowMainMenu(!settingsMenuItems.some(item => location.pathname.startsWith(item.path)))
        }
    }, [location.pathname, permissions])

    const filteredSubmenu = submenu => submenu?.filter(sub => sub.permissions?.some(role => permissions?.includes(role)))

    const toggleMenu = label => {
        setExpandedMenus(prev => (prev.includes(label) ? prev.filter(m => m !== label) : [...prev, label]))
    }

    const isActive = path => {
        if (path === '/') {
            return location.pathname === '/'
        }
        return location.pathname.startsWith(path)
    }

    const isParentActive = submenu => submenu?.some(item => isActive(item.path))

    const sidebar = (
        <div className="container">
            {isShowMainMenu ? (
                <>
                    <Link to="/">
                        <img src={careviahLogo} height={38} />
                    </Link>
                    <br />
                    <Typography variant="inputLabel" color="text.secondary">
                        Select Branch
                    </Typography>
                    <Select fullWidth value={branchId || ''} onChange={e => setBranchId(e.target.value)} style={{marginTop: 4}}>
                        {branches?.map(item => (
                            <MenuItem key={item.branch_id} value={item.branch_id}>
                                {item.branch_name}
                            </MenuItem>
                        ))}
                    </Select>
                    <br />
                    {filteredMenuItems.map(({label, icon, path, hasSubmenu, submenu}) => {
                        const visibleSubmenu = filteredSubmenu(submenu)
                        if (hasSubmenu && visibleSubmenu.length === 0) return null

                        return (
                            <div key={label}>
                                {hasSubmenu ? (
                                    <div className={`item ${isParentActive(visibleSubmenu) ? 'active' : ''}`} onClick={() => toggleMenu(label)}>
                                        <div className="icon">{icon}</div>
                                        <div className="item-label">{label}</div>
                                    </div>
                                ) : (
                                    <Link to={path} className={`item ${isActive(path) ? 'active' : ''}`}>
                                        <div className="icon">{icon}</div>
                                        <div className="item-label">{label}</div>
                                    </Link>
                                )}
                                {hasSubmenu && expandedMenus.includes(label) && (
                                    <div className="submenu">
                                        {visibleSubmenu.map(({label: subLabel, path: subPath}) => (
                                            <Link key={subLabel} to={subPath} className={`subitem ${isActive(subPath) ? 'active' : ''}`}>
                                                <div className="subitem-label">{subLabel}</div>
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )
                    })}
                    <div className="bottom-navigation">
                        {bottomMenuItems.map(({label, icon, path}) => (
                            <div key={label}>
                                <Link to={path} className={`item ${isActive(path) ? 'active' : ''}`}>
                                    <div className="icon">{icon}</div>
                                    <div className="item-label">{label}</div>
                                </Link>
                            </div>
                        ))}
                        {mobile && (
                            <Link className="item" onClick={() => setLogout(true)}>
                                <div className="icon" style={{color: '#D32F2F'}}>
                                    <Logout />
                                </div>
                                <div className="item-label" style={{color: '#D32F2F'}}>
                                    Log Out
                                </div>
                            </Link>
                        )}
                    </div>
                </>
            ) : (
                <div>
                    <Link to="/">
                        <img src={careviahLogo} height={38} />
                    </Link>
                    <br />
                    <br />
                    <br />
                    <div className="back-container">
                        <Link className="back-button" to="/">
                            <ArrowBack style={{color: '#ffffff'}} fontSize="small" />
                        </Link>
                        <Typography variant="body1" color="text.primary" style={{marginBottom: -2.5}}>
                            Back
                        </Typography>
                    </div>
                    {filteredSettingMenuItems.map(({label, icon, path}) => (
                        <div key={label}>
                            <Link to={path} className={`item ${isActive(path) ? 'active' : ''}`}>
                                <div className="icon">{icon}</div>
                                <div className="item-label">{label}</div>
                            </Link>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )

    return (
        <>
            {mobile ? (
                <Modal open={mobileSidebar} onClose={() => setMobileSidebar(false)}>
                    {sidebar}
                </Modal>
            ) : (
                sidebar
            )}
        </>
    )
}
